<template>
  <div class="item-option">
    <div
      class="item-option--head pb-1 d-flex flex-row flex-nowrap justify-space-between align-center"
    >
      <div>
        <h4 class="option-title font-weight-bold">{{ option.title }}</h4>
        <span class="subtitle grey--text d-inline-block" v-if="subtitle">{{
          subtitle
        }}</span>
      </div>
      <v-chip
        v-if="option.main || option.mandatory || option.min"
        round
        label
        x-small
        >{{ $t('itemOptions.mandatory') }}</v-chip
      >
    </div>
    <div class="item-option--items">
      <div>
        <div
          class="item-option--item d-flex flex-row align-center justify-space-between"
          :class="{ clickable: type === 'choose' && optionItem.active }"
          v-for="(optionItem, index) of option.optionItems"
          :key="optionItem.id"
          @click="onOptionClick(optionItem)"
        >
          <div>
            <span class="d-block pr-3">{{ optionItem.title }}</span>
            <span
              class="price"
              v-if="
                !hidePrices &&
                (!option.main
                  ? optionItem.discountedCost || optionItem.cost
                  : (optionItem.discountedCost || optionItem.cost) -
                    (item.discountedPrice || item.price)) !== 0
              "
            >
              {{ getPrice(optionItem) }}
              <v-chip
                dark
                label
                x-small
                class="discount-tag chips--low-padding font-weight-regular"
                v-if="optionItem.discountedCost !== null"
                color="#ff6f00"
                >-%{{
                  calcDiscount(optionItem.cost, optionItem.discountedCost)
                }}</v-chip
              >
            </span>
          </div>
          <div>
            <template v-if="option.type === 'add'">
              <v-btn
                v-if="!selectedItems[optionItem.id]"
                @click="addToAdditional(optionItem)"
                :disabled="maxReached"
                depressed
                rounded
                small
              >
                Agregar
              </v-btn>
              <vue-numeric-input
                v-else
                class="counter"
                v-model="selectedItems[optionItem.id]"
                :min="0"
                :max="
                  !option.max
                    ? undefined
                    : selectedCount >= option.max
                    ? selectedItems[optionItem.id]
                    : undefined
                "
                :disabled="!optionItem.active"
                @input="onChange"
              />
            </template>
            <template v-else>
              <v-checkbox
                checked-color="#ff7900"
                :input-value="selectedItems[optionItem.id]"
                :disabled="
                  !optionItem.active ||
                  (maxReached && max > 1 && !selectedItems[optionItem.id])
                "
                v-if="max !== 1"
                hide-details
                class="mt-0"
                dense
              />
              <v-radio-group
                v-else
                v-model="selectedItems[optionItem.id]"
                hide-details
                class="mt-0"
              >
                <v-radio
                  style="pointer-events: none"
                  checked-color="#ff7900"
                  :value="1"
                  @click="onRadioClick"
                  :disabled="
                    !optionItem.active ||
                    (maxReached && max > 1 && !selectedItems[optionItem.id])
                  "
                  hide-details
                  class="mt-0"
                  dense
                />
              </v-radio-group>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import { IRestaurantSettings } from '../../api/services/restaurants.service';
  import { IItem } from '../../interfaces/IItem';
  import { IOption } from '../../interfaces/IOption';
  import { OptionType } from '../../enums/OptionType';
  import { TaxDisplayMode } from '../../enums/TaxDisplayMode';
  import {
    applyDiscount,
    calcDiscount,
    displayPrice,
    notNull,
  } from '../../utils';
  import { IOptionItem } from '../../interfaces/IOptionItem';
  import VueNumericInput from 'vue-numeric-input';
  import { IOrderItemOption } from '../../interfaces/IOrderItemOption';

  @Component({
    components: {
      VueNumericInput,
    },
  })
  export default class ItemOption extends Vue {
    @Prop() currency: ICurrency;
    @Prop() restaurant: IRestaurant;
    @Prop() settings: IRestaurantSettings;
    @Prop() item: IItem;
    @Prop() option: IOption;
    @Prop({ default: true }) readonly: boolean;
    @Prop({ default: false }) isEditing: boolean;
    @Prop({ default: false }) hidePrices: boolean;
    selectedItems: { [id: number]: number } = {};
    applyDiscount = applyDiscount;
    calcDiscount = calcDiscount;
    displayPrice = displayPrice;

    async created() {
      if (!this.isEditing && this.option.main) {
        const first = this.option.optionItems.find((value) => value.active);
        if (first) this.onOptionClick(first);
      }
    }

    onRadioClick(e: MouseEvent) {
      e.preventDefault();
    }

    get valid() {
      switch (this.option.type) {
        case OptionType.Add:
          return (
            (!this.option.min || this.selectedCount >= this.option.min) &&
            (!this.option.max || this.selectedCount <= this.option.max)
          );
        case OptionType.Choose:
          const min =
            this.option.min ||
            (this.option.main || this.option.mandatory ? 1 : 0);
          return Object.keys(this.selectedItems).length >= min;
      }
    }

    applyTax(val: number) {
      if (
        this.hasTax &&
        (this.settings.taxMode === TaxDisplayMode.AddAndShow ||
          this.settings.taxMode === TaxDisplayMode.SilentAdd)
      ) {
        val *= 1 + this.restaurant.tax / 100;
      }
      return val;
    }

    get hasTax() {
      return (
        this.settings.taxMode !== TaxDisplayMode.NoTax && this.restaurant.tax
      );
    }

    onOptionClick(item: IOptionItem) {
      if (this.type !== OptionType.Choose || !item.active) return;

      if (
        !this.selectedItems[
          item.id
        ] /*||
        (this.type === OptionType.Choose &&
          this.max === 1 &&
          (this.option.mandatory || this.option.main))*/
      ) {
        if (this.maxReached) {
          if (this.max > 1) return;

          this.selectedItems = {};
        }
        Vue.set(this.selectedItems, item.id, 1);
      } else {
        if (this.option.main) return;
        Vue.delete(this.selectedItems, item.id);
      }

      this.onChange();
    }

    public initFromOrderItemOptions(oIOs: IOrderItemOption[]) {
      this.selectedItems = {};
      for (const oIO of oIOs) {
        Vue.set(this.selectedItems, oIO.optionItemId, oIO.val);
      }
    }

    onChange() {
      this.$emit('change', this.selectedItems);
    }

    getPrice(optionItem: IOptionItem) {
      // type === 'choose' ? '+' : ''
      let price =
        !this.option.main || this.settings.variantsFullPrice
          ? notNull(optionItem.discountedCost, optionItem.cost)
          : notNull(optionItem.discountedCost, optionItem.cost) -
            notNull(this.item.discountedPrice, this.item.price);

      let symbol =
        this.option.type === OptionType.Add ||
        (this.option.main && this.settings.variantsFullPrice)
          ? ''
          : price > 0
          ? '+'
          : '-';

      return `${symbol}${this.currency.symbol}${displayPrice(
        this.applyTax(Math.abs(price)),
        this.settings.showDecimals
      )}`;
    }

    addToAdditional(optionItem: IOptionItem) {
      Vue.set(this.selectedItems, optionItem.id, 1);
      this.onChange();
    }

    get subtitle() {
      if (!this.option.multiple) return false;

      if (this.option.max && this.option.min) {
        if (this.option.max !== this.option.min)
          return this.$t('itemOptions.minMax', {
            min: this.option.min,
            max: this.option.max,
          });
        else return this.$t('itemOptions.exact', { min: this.option.min });
      } else if (this.option.max)
        return this.$t('itemOptions.max', { max: this.option.max });
      else if (this.option.min)
        return this.$t('itemOptions.min', { min: this.option.min });

      return false;
    }

    get selectedCount() {
      if (this.option.type === OptionType.Choose)
        return Object.keys(this.selectedItems).length;
      else {
        let count = 0;
        for (const id in this.selectedItems) {
          const val = this.selectedItems[id];
          count += val;
        }
        return count;
      }
    }

    get maxReached() {
      return this.max && this.selectedCount >= this.max;
    }

    get max() {
      return this.option.type === OptionType.Choose && !this.option.multiple
        ? 1
        : this.option.max;
    }

    get type() {
      return this.option.type;
    }
  }
</script>
